import { produce } from 'immer'
import isEqual from 'lodash/isEqual'
import { intersectionBy, unique, uniqueBy } from '@dhl/utils'
import { TimeWindow } from '@dhl/inputs'

import { liftSelectorsWithArgument } from '~/util/toGlobalSelectors'
import { SelectableHandOffOption, PickupRangeBound } from '~/basket/model/reducers'
import { ShipmentState } from '~/basket/model/session/reducers'
import { capabilityReducer, capabilitySelectors, CapabilityState } from './capability/reducers'
import { ACTION, Action } from './actions'

/** *****************
 * Reducers
 ***************** */

export type CapabilitiesState = Record<number, CapabilityState>

export const reducers = (state: CapabilitiesState = {}, action: Action): CapabilitiesState => {
  switch (action.type) {
    case ACTION.UPDATE_CAPABILITIES_REQUESTED:
    case ACTION.UPDATE_CAPABILITIES_SUCCESS:
      return produce<CapabilitiesState>(state, (draft) => {
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        draft[action.payload.shipmentId] = capabilityReducer(draft[action.payload.shipmentId], action)
      })
    case ACTION.UPDATE_CAPABILITIES_FAILURE:
      return produce<CapabilitiesState>(state, (draft) => {
        // eslint-disable-next-line no-param-reassign
        draft[action.payload.shipmentId] = capabilityReducer(draft[action.payload.shipmentId], action)
      })
    case ACTION.PREFILL_CAPABILITIES_AFTER_FAILED_PAYMENT:
      return { ...action.payload }
    default: {
      return state
    }
  }
}

/** *****************
 * Selectors
 *
 * Most of these selectors are not necessary if we ask the customer only once for their own address.
 *
 ***************** */

const isLoadingCapabilities = (state: CapabilitiesState): boolean =>
  // eslint-disable-next-line radix
  Object.keys(state).some((shipmentId) => capabilitySelectors.isLoadingCapability(state[parseInt(shipmentId)]))

const getCapabilitiesError = (state: CapabilitiesState): Error | null | undefined =>
  Object.keys(state)
    // eslint-disable-next-line radix
    .map((shipmentId) => capabilitySelectors.getError(state[parseInt(shipmentId)]))
    .find(Boolean)

const getHandOffOptionsByCarrier = (
  capabilitiesState: CapabilitiesState,
  carrierKey: string,
  shipments: ShipmentState[]
): SelectableHandOffOption[] => {
  const getKey = ({ key }: SelectableHandOffOption): string => key

  const [first, ...rest] = Object.keys(capabilitiesState).map((shipmentId) =>
    // @ts-ignore
    capabilitySelectors.getHandOffOptionsByCarrierAndShipment(capabilitiesState[shipmentId])(carrierKey)
  )

  const uniqueHandOffOptions = uniqueBy((rest || []).reduce(intersectionBy(getKey), first), getKey)

  // Pickup is only available when:
  // 1. There is only one shipment
  // 2. When there is only one sender address (so, the same address for all shipments)

  // Only check non-empty addresses, so the pickup option doesnt disappear needlessly
  const shipmentsToCheck = shipments.filter((shipment) => {
    const senderAddress = shipment.shipper.address
    return (
      senderAddress.street !== '' ||
      senderAddress.houseNumber !== '' ||
      senderAddress.postalCode !== '' ||
      senderAddress.city !== ''
    )
  })

  const allSenderAddressesAreEqual =
    shipmentsToCheck.length < 2 ||
    shipmentsToCheck.slice(1).reduce(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (allEqual, shipment, index): any =>
        allEqual && isEqual(shipment.shipper.address, shipmentsToCheck[index].shipper.address),
      true
    )

  const result = uniqueHandOffOptions.filter(
    (handOffOption) => handOffOption.key !== 'PICKUP' || allSenderAddressesAreEqual
  )

  return result
}

const getPickupDatesByCarrier = (state: CapabilitiesState, carrierKey: string): string[] => {
  const [first, ...rest] = Object.keys(state).map((shipmentId) =>
    // @ts-ignore
    capabilitySelectors.getPickupDatesByCarrier(state[shipmentId])(carrierKey)
  )

  return unique(
    (rest || []).reduce(
      intersectionBy((x) => x),
      first
    )
  )
}

const getPickupRangeBoundByCarrier = (
  state: CapabilitiesState,
  carrierKey: string,
  pickupDate: string | null
): PickupRangeBound | null => {
  const reduce = (left: PickupRangeBound | null, right: PickupRangeBound | null): PickupRangeBound | null => {
    if (!left || !right) {
      return null
    }

    return {
      min: Math.max(left.min, right.min),
      max: Math.min(left.max, right.max),
    }
  }

  return Object.keys(state)
    .map((shipmentId) =>
      // @ts-ignore
      capabilitySelectors.getPickupRangeBoundByCarrier(state[shipmentId])(carrierKey, pickupDate)
    )
    .reduce(reduce, { min: Number.MIN_VALUE, max: Number.MAX_VALUE })
}

const getTimeWindows = (state: CapabilitiesState, carrierKey: string): TimeWindow[] => {
  const getKey = (timeWindow: TimeWindow) => `${timeWindow.start.unix()}-${timeWindow.end.unix()}`

  const [first, ...rest] = Object.keys(state).map((shipmentId) =>
    // @ts-ignore
    capabilitySelectors.getTimeWindows(state[shipmentId])(carrierKey)
  )

  return uniqueBy((rest || []).reduce(intersectionBy(getKey), first), getKey)
}

export const selectors = {
  ...liftSelectorsWithArgument(
    (state: CapabilitiesState, shipmentId: number) => state[shipmentId],
    capabilitySelectors
  ),
  getHandOffOptionsByCarrier,
  getPickupDatesByCarrier,
  getPickupRangeBoundByCarrier,
  getTimeWindows,
  isLoadingCapabilities,
  getCapabilitiesError,
}
